import API, { graphqlOperation } from "@aws-amplify/api";
import { Amplify, Hub } from "@aws-amplify/core";
import { useFonts } from "@expo-google-fonts/open-sans";
import useAPI from "@toothfairy/shared-api/useApi";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import "highlight.js/styles/github.css";
import "jsoneditor/dist/jsoneditor.css";
import "prismjs/themes/prism.css";
import "rc-slider/assets/index.css";
import React, { Suspense, useEffect, useState } from "react";
import { Dimensions, Platform, View } from "react-native";
import { makeVar } from "react-reactive-var";
import envConfig from "./envConfig";
import "./i18n.config";
import { makeGraphQLRequest } from "./src/API";
import API_Consumption from "./src/API/API_Consumption";
import config from "./src/Auth/aws-exports";
import ElectronAuthPrompt from "./src/Components/ElectronAuth";
import ToothFairyLoadingPlaceholder from "./src/Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import { default as AppConfig, default as Constants } from "./src/Constants";
import AppNotification from "./src/Hooks/useNotification";
import appReducer from "./src/Redux/appReducer";
import initialState from "./src/Redux/initialState";
import { Router, useHistory } from "./src/Router";
import Routes, { routes } from "./src/Router/routes";
import AuthScreen from "./src/Screens/Auth/AuthScreen";
import ChatsScreenContainer from "./src/Screens/Chats/ChatsScreenContainer";
import MainViewContainer from "./src/Screens/MainViewContainer";
import {
  listSubscriptionBaseConfigs,
  listSubscriptionPricingPlans,
  usersByOwner,
  workspacesByUser,
} from "./src/graphql/queries";

const isElectron = !!window?.electronAPI;

Amplify.configure(config);
// AppAuth.Auth.configure(config)
API.configure(config);

export const AppRootContext = React.createContext({});
API.Auth.configure(config);
console.log("HUB--", Hub);
export const showSideMenu = makeVar(
  Dimensions.get("window").width > Constants.screenResponsiveWidth
);
export const forcedClosed = makeVar(false);
export const pinnedSideMenu = makeVar(false);
export const isOllamaRunning = makeVar(false);
export const oLlamaModelLoaded = makeVar(null);
export const currentUserId = makeVar(null);
export const lastPathInWorkspace = makeVar({
  workspaceid: null,
  path: null,
});
export const isUserDelinquent = makeVar(false);
export const isUserOnTrial = makeVar(false);
export const isFirstTrialStarted = makeVar(false);
export const isUserWithNoPaymentMethod = makeVar(false);
export const isUserEnabledToOperate = makeVar(false);
export const isBillingAddressSet = makeVar(false);
// CHANGE HERE -> !
export const isConnectedToCloud = makeVar(true);
export const displayedToasties = makeVar(null);
export const reservedConfigs = makeVar(null);
export const userType = makeVar(null);
export const isSSO = makeVar(false);
export const isElectronRunning = makeVar(isElectron);
export const electronAppPath = makeVar("");
//

const App = () => {
  // Amplify.Logger.LOG_LEVEL = "DEBUG";
  const [isLocalDBReady, setLocalDBReady] = useState(true);
  const [serverWorkspaces, setServerWorkspaces] = useState([]);
  const [authUserSub, setAuthUserSub] = useState(null);
  const [dataSyncReady, setDataSyncReady] = useState(false);
  const [subscriptionsEstablished, setSubscriptionsEstablished] =
    useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isFederatedSignInProgress, setFederatedSignInProgress] =
    useState(false);
  const history = useHistory();
  const {
    data: stripeCustomerData,
    loading: stripeCustomerAPIInProgress,
    apiRequest: fetchStripeCustomer,
  } = useAPI(API_Consumption.getStripeCustomer);

  // --- CORE CHANGE AREA: Authentication Logic ---
  const onCognitoUserAuthenticated = async () => {
    try {
      const [authUserResult, resultFromStripe] = await Promise.all([
        getCurrentAuthenticatedUser(),
        fetchStripeCustomer(),
      ]);
      if (
        authUserResult?.username?.startsWith("google_") ||
        authUserResult?.username?.startsWith("azure")
      ) {
        setAuthUserSub(
          authUserResult?.signInUserSession?.idToken?.payload?.sub
        );
        await fetchDataForSelectiveSync(
          authUserResult?.signInUserSession?.idToken?.payload?.sub
        );
        isSSO(authUserResult?.username?.startsWith("azure"));
        // create the user in dynamodb using name and surname
        // associate to a workspace based on the domain name
        // give them access based on the role tf_user
        // then they can use the app
      } else {
        setAuthUserSub(authUserResult.username);
        await fetchDataForSelectiveSync(authUserResult.username);
      }
      if (resultFromStripe) {
        isUserDelinquent(resultFromStripe.delinquent);
        isFirstTrialStarted(resultFromStripe.trialEndDate != null);
        isBillingAddressSet(resultFromStripe.address !== null);
        isUserOnTrial(
          resultFromStripe.trialEndDate !== null &&
            resultFromStripe.trialEndDate * 1000 > Date.now()
        );
        isUserWithNoPaymentMethod(
          resultFromStripe.invoice_settings.default_payment_method === null
        );
        isUserEnabledToOperate(
          !resultFromStripe.delinquent &&
            resultFromStripe.invoice_settings.default_payment_method !== null
        );
      }
      fetchDataForConfig();
      fetchDataForWorkspaceBaseConfig();
    } catch (error) {
      if (isConnectedToCloud()) throw error;
      else {
        console.log("Attempting to proceed offline");
      }
    }
  };
  const {
    currentView,
    setCurrentView,
    getCurrentAuthenticatedUser,
    attemptSignInWithGoogle,
  } = AppAuth.useAuth(config, onCognitoUserAuthenticated);
  // **1. Define Core Post-Authentication Logic (Closely mirroring your original onCognitoUserAuthenticated)**
  //    We need this logic accessible after BOTH web auth and Electron auth.
  const performPostAuthenticationSetup = async (
    authenticatedUserSub,
    isSSOUser = false
  ) => {
    console.log(
      "Performing post-authentication setup for sub:",
      authenticatedUserSub
    );
    setAuthUserSub(authenticatedUserSub);

    try {
      // const authUserResult = getCurrentAuthenticatedUser();
      // console.log("User result", authUserResult);
      const [resultFromStripe] = await Promise.all([fetchStripeCustomer()]);
      // console.log("authUserResult", authUserResult);
      console.log("resultFromStripe", resultFromStripe);
      // const [authUserResult, resultFromStripe] = await Promise.all([
      //   getCurrentAuthenticatedUser(),
      //   fetchStripeCustomer(),
      // ]);
      // Set state immediately based on known info
      setAuthUserSub(authenticatedUserSub);
      isSSO(isSSOUser); // Update reactive var if needed
      await fetchDataForSelectiveSync(authenticatedUserSub);
      if (resultFromStripe) {
        isUserDelinquent(resultFromStripe.delinquent);
        isFirstTrialStarted(resultFromStripe.trialEndDate != null);
        isBillingAddressSet(resultFromStripe.address !== null);
        isUserOnTrial(
          resultFromStripe.trialEndDate !== null &&
            resultFromStripe.trialEndDate * 1000 > Date.now()
        );
        isUserWithNoPaymentMethod(
          resultFromStripe.invoice_settings.default_payment_method === null
        );
        isUserEnabledToOperate(
          !resultFromStripe.delinquent &&
            resultFromStripe.invoice_settings.default_payment_method !== null
        );
      }
      fetchDataForConfig();
      fetchDataForWorkspaceBaseConfig();
      // Crucially, change the view only after essential data is ready
      // The main rendering logic checks for stripeCustomerData anyway
      setCurrentView("mainNav");
      console.log("Post-authentication setup complete, moving to mainNav.");
      setTimeout(() => {
        history.push(routes.workspaces);
      }, [1000]);
    } catch (error) {
      console.error("Error during post-authentication setup:", error);
      // Handle error appropriately - maybe reset auth state, show error message
      setAuthUserSub(null); // Reset user sub on error
      setCurrentView("auth"); // Go back to auth screen on error
      // Consider showing a toast message here
      // if (isConnectedToCloud()) throw error; // Re-throw if online? Be careful with this.
      // else { console.log("Attempting to proceed offline"); }
    }
  };

  // **2. Original Web Authentication Callback**

  // **3. Hook into AppAuth (Keep as is)**

  let [fontsLoaded] = useFonts({
    normalFont: require("./src/Fonts/Gilroy-Regular.woff2"),
    boldFont: require("./src/Fonts/Gilroy-SemiBold.woff2"),
  });
  const _isInitialisationInProgress =
    currentView === "initializing" ||
    stripeCustomerAPIInProgress ||
    (isElectron && isLoggingIn) ||
    (!isLocalDBReady && currentView !== "auth");
  useEffect(() => {
    Hub.listen("datastore", async (hubData) => {
      const { event, data } = hubData.payload;
      console.log("__", event);
      if (event === "subscriptionsEstablished") {
        setSubscriptionsEstablished(true);
      }
      if (event === "networkStatus") {
        isConnectedToCloud(data.active); //@todo: use makeVar
      }
    });
  }, []);
  // **4. Electron Detection**
  // **5. useEffect for Electron Listeners and Initial Check**
  useEffect(() => {
    // --- This effect ONLY runs if in Electron ---
    if (!isElectron) {
      // If not in electron, AppAuth.useAuth likely handles the initial check.
      // If AppAuth doesn't handle initial check, you might need to call
      // getCurrentAuthenticatedUser() here for the web flow.
      // console.log("Not in Electron, skipping Electron-specific setup.");
      return;
    }

    console.log(
      "Running in Electron, setting up listeners and initial check..."
    );

    // --- Electron Listener Handlers ---
    const handleElectronLoginSuccess = async (claims) => {
      console.log("Electron Auth: Login successful, received claims:", claims);
      try {
        setIsLoggingIn(true);
        if (claims && claims.sub) {
          // Determine if SSO based on claims (adjust logic if needed)
          // Cognito ID tokens often include an 'identities' claim for federation
          const claimIdentities =
            typeof claims.identities == "string"
              ? JSON.parse(claims.identities)
              : claims.identities;
          console.log("Claimed identities", claimIdentities);
          const isSSOUser = claimIdentities
            ? claimIdentities.some((id) => id.providerName === "Azure")
            : false; // Example check for Azure
          // Call the SAME setup logic used by web auth
          await performPostAuthenticationSetup(claims.sub, isSSOUser);
        } else {
          console.error("Electron Auth: Success callback missing claims.sub");
          setCurrentView("auth"); // Go back to auth on unexpected data
        }
      } catch (error) {
      } finally {
        setIsLoggingIn(false);
      }
    };

    const handleElectronLoginFailure = async (errorMsg) => {
      console.error("Electron Auth: Login failed:", errorMsg);
      if (errorMsg?.toString()?.includes("nonce mismatch")) {
        console.log("Attempting to recover from error on nonce");
        setIsLoggingIn(true);
        await window.electronAPI.login();
        setIsLoggingIn(false);
      } else {
        setAuthUserSub(null); // Clear any potential partial state
        setCurrentView("auth"); // Stay on auth view
        // Add user feedback (e.g., Toast) if desired
      }
    };

    const handleElectronLoggedOut = () => {
      console.log("Electron Auth: Logout confirmed.");
      setAuthUserSub(null);
      // Reset other relevant state/reactive vars if needed
      setCurrentView("auth"); // Go back to auth view
    };

    const handleElectronForceLogout = (message) => {
      console.warn("Electron Auth: Forced logout:", message);
      setAuthUserSub(null);
      // Reset other relevant state/reactive vars
      setCurrentView("auth");
    };

    // --- Attach Electron Listeners ---
    window.electronAPI.onLoginSuccess(handleElectronLoginSuccess);
    window.electronAPI.onLoginFailure(handleElectronLoginFailure);
    window.electronAPI.onLoggedOut(handleElectronLoggedOut);
    window.electronAPI.onForceLogout(handleElectronForceLogout);
    console.log("Electron Auth: Listeners attached.");

    // --- Electron Initial State Check ---
    const checkElectronInitialAuthState = async () => {
      console.log("Electron Auth: Checking initial auth state...");
      try {
        // Check if we can get a token (this implicitly tries refresh)
        const accessToken = await window.electronAPI.getAccessToken();
        console.log("Retrieved access tokens", accessToken);
        if (accessToken) {
          console.log("Electron Auth: Access token found, getting claims...");
          const claims = await window.electronAPI.getUserClaims(); // Get user details

          if (claims && claims.sub) {
            console.log(
              "Electron Auth: Session restored via stored tokens/claims."
            );
            const _claims =
              typeof claims.identities == "string"
                ? JSON.parse(claims.identities)
                : claims.identities;
            let isSSOUser = false;
            // Call the SAME setup logic
            try {
              isSSOUser = claims.identities
                ? _claims.some((id) => id.providerName === "Azure")
                : false; // Example check
            } catch (error) {
              isSSOUser = false;
              console.log("SSO check failed", error);
            }

            await performPostAuthenticationSetup(claims.sub, isSSOUser);
            setLocalDBReady(true);
            return; // Exit early on success
          } else {
            console.warn(
              "Electron Auth: Had access token but failed to get valid claims. Forcing logout."
            );
            // Force logout / clear tokens if claims are invalid despite having a token
            await window.electronAPI.logout(); // Trigger full logout flow
            setAuthUserSub(null);
            setCurrentView("auth");
          }
        } else {
          console.log("Electron Auth: No active session found via tokens.");
          setAuthUserSub(null);
          setCurrentView("auth");
        }
      } catch (e) {
        console.error("Electron Auth: Error checking initial auth state:", e);
        setAuthUserSub(null); // Ensure clean state on error
        setCurrentView("auth");
      } finally {
        // This might be slightly premature if performPostAuthenticationSetup is still running
        // Consider moving this inside the setup function's completion?
        // Or rely on the main rendering logic's check. For now, let's leave it here.
        // _setIsInitialisationInProgress(false); // -> Let's remove direct control of this flag from here
        // It seems tied to currentView and data readiness in the rendering logic already.
        console.log("Electron Auth: Initial state check complete.");
      }
    };

    checkElectronInitialAuthState();

    // --- Cleanup Electron Listeners ---
    return () => {
      console.log("Electron Auth: Cleaning up listeners.");
      window.electronAPI.removeAllListeners("login-success");
      window.electronAPI.removeAllListeners("login-failure");
      window.electronAPI.removeAllListeners("logged-out");
      window.electronAPI.removeAllListeners("force-logout");
    };

    // Dependencies: Include functions/state used *inside* the Electron-specific logic
  }, [isElectron, currentView]);
  useEffect(() => {
    if (isElectron && window?.electronAPI) {
      window.electronAPI.getPath();
      window.electronAPI.on("app-path", (path) => {
        electronAppPath(path);
      });
    }
  }, [isElectron]);
  useEffect(() => {
    Hub.listen("auth", async (hubData) => {
      const { event, data } = hubData.payload;
      if (event === "signIn" && data?.username?.startsWith("google_")) {
        window.location.reload();
      } else if (
        event === "signIn_failure" &&
        data?.message === "invalid_grant"
      ) {
        setFederatedSignInProgress(true);
        console.log("__auth__attempt_init");
        const result = await attemptSignInWithGoogle();
      }
    });
  }, []);
  // useEffect(() => {
  //   if (authUserSub) fetchDataForSelectiveSync(authUserSub);
  // }, [isConnectedToCloud(), authUserSub]);
  const fetchDataForSelectiveSync = async (ownerId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const userData = await makeGraphQLRequest(
          // <-- Use the wrapper
          graphqlOperation(usersByOwner, {
            owner: ownerId,
          })
        );

        const userID = userData?.data?.usersByOwner?.items?.[0]?.id;

        const workspaceData = await makeGraphQLRequest(
          graphqlOperation(workspacesByUser, {
            userID,
          })
        );

        const workspaces = workspaceData?.data?.workspacesByUser?.items
          ?.filter((u) => u?._deleted != true)
          ?.map((u) => u.workspaceID);
        currentUserId(userID);
        setServerWorkspaces(workspaces);
        setDataSyncReady(true);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const fetchDataForConfig = async () => {
    const result = await makeGraphQLRequest(
      graphqlOperation(listSubscriptionPricingPlans, {})
    );
    const config = result?.data?.listSubscriptionPricingPlans;
    reservedConfigs({ ...reservedConfigs(), plans: config?.items });
  };

  const fetchDataForWorkspaceBaseConfig = async () => {
    const result = await makeGraphQLRequest(
      graphqlOperation(listSubscriptionBaseConfigs, {})
    );

    const config = result?.data?.listSubscriptionBaseConfigs;
    reservedConfigs({
      ...reservedConfigs(),
      workspaceBaseConfigs: config?.items,
    });
  };

  useEffect(() => {
    if (currentView === "auth") setLocalDBReady(false);
  }, [currentView]);

  return (
    <AppRootContext.Provider
      value={{
        setCurrentView,
      }}
    >
      <AppNotification.NotificationProvider>
        <AppTheme.ThemeManager>
          <AppToast.ToastProvider
            {...AppConfig.notificationConfig}
            // offset for both top and bottom toasts..
          >
            <AppStateManager.AppProvider
              initialState={initialState}
              appReducer={appReducer}
              envConfig={envConfig}
            >
              {/* This theme provider is required for emotion. It conflicts with the AppTheme. */}
              {/*<ThemeProvider theme={generateLightTheme.theme}>*/}
              <AppUser.UserProvider>
                {_isInitialisationInProgress && (
                  <ToothFairyLoadingPlaceholder brandedAnimation />
                )}
                {!_isInitialisationInProgress &&
                  currentView === "auth" &&
                  isElectron && (
                    <ElectronAuthPrompt /> // Use the simple Electron prompt
                  )}
                {!isConnectedToCloud() ? (
                  <AppBaseScreen isScrollable={Platform.OS === "web" && false}>
                    <View>
                      <Router>
                        <ChatsScreenContainer />
                      </Router>
                    </View>
                  </AppBaseScreen>
                ) : (
                  <>
                    {!_isInitialisationInProgress &&
                      currentView === "auth" &&
                      !isElectron && (
                        // Use the original AuthScreen for web
                        <Suspense fallback={<div>Loading...</div>}>
                          <AuthScreen
                            isFederatedSignInProgress={
                              isFederatedSignInProgress
                            }
                            setCurrentView={setCurrentView}
                            // This prop might not be needed if AppAuth.useAuth handles it,
                            // but passing it ensures the web flow triggers the callback.
                            onCognitoUserAuthenticated={
                              onCognitoUserAuthenticated
                            }
                            setFederatedSignInProgress={
                              setFederatedSignInProgress
                            }
                          />
                        </Suspense>
                      )}
                    {currentView === "mainNav" &&
                      authUserSub &&
                      serverWorkspaces &&
                      dataSyncReady &&
                      stripeCustomerData && (
                        <MainViewContainer
                          _isInitialisationInProgress={
                            _isInitialisationInProgress
                          }
                          stripeCustomerData={stripeCustomerData}
                          isLocalDBReady={isLocalDBReady}
                          authUserSub={authUserSub}
                          isConnectedToCloud={isConnectedToCloud()}
                          serverWorkspaces={serverWorkspaces}
                          currentView={currentView}
                        >
                          <Routes
                            setCurrentView={setCurrentView}
                            currentView={currentView}
                          />
                        </MainViewContainer>
                      )}
                    {!_isInitialisationInProgress &&
                      currentView !== "auth" &&
                      !(
                        currentView === "mainNav" &&
                        authUserSub &&
                        serverWorkspaces &&
                        dataSyncReady &&
                        stripeCustomerData
                      ) && <ToothFairyLoadingPlaceholder />}
                  </>
                )}
              </AppUser.UserProvider>
              {/*</ThemeProvider>*/}
            </AppStateManager.AppProvider>
          </AppToast.ToastProvider>
        </AppTheme.ThemeManager>
      </AppNotification.NotificationProvider>
    </AppRootContext.Provider>
  );
};

//export default withAuthenticator(App);
export default App;
